import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Grid,
  Switch,
  Typography,
  FormControlLabel,
  useTheme,
  CircularProgress,
  Chip,
  Stack,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  IconButton,
  ListItem,
  ListItemText,
  Dialog,
  Slide,
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import DownloadIcon from "@mui/icons-material/Download";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import Snackbar from "@mui/material/Snackbar";
import { useNotificationState } from "@pages/Contents/Corporate/Notifications/context/NotificationContext";
import { useTranslation } from "react-i18next";
import { ListNotificationType } from "../../../../../../interfaces/Common";
import {
  getFlagKey,
  getUploadKey,
  // handleFileChange,
} from "@pages/Contents/Corporate/Notifications/utils/utils";
import { InputCst, SelectCst } from "@components/InputCst";
import { ActionUploadDownloadIterface } from "@components/ModalDownloadUploadCst/types";
import {
  ResponseEvents,
  Step2Props,
  TagInterface,
  ValuesInterface,
} from "@pages/Contents/Corporate/Notifications/types";
import { useFetchTable } from "@hooks/useFetchDataTable";
import { getZipWithoutDownload, ServicesURL } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import { useFetchDataDetail, useFetchDetail } from "@hooks/useFetchDetail";
import { useParams } from "react-router-dom";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { VisuallyHiddenInput } from "@components/InputCst/UploadInput";
import {
  bytesToMB,
  handleDownloadFile,
  handleMultiFileChange,
  handleMultiFileDelete,
} from "@pages/Contents/BadgeAndCertificates/utils/utils";
import DeleteIcon from "@mui/icons-material/Delete";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import UploadIcon from "@mui/icons-material/Upload";
import { unzipZipFile } from "@hooks/useUnzip";
import ButtonCst from "@components/ButtonCst";
import MailEditor from "../MailEditor/MailEditor";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import {
  convertHtmlToPuckData,
  isMjmlTemplate,
} from "../MailEditor/importer/htmlImportConverter";
import { generateEmailHtml } from "../MailEditor/utils/emailHtmlConverter";
import { useTranslateInput } from "@hooks/useTranslateInput";
import useSWRMutation from "swr/mutation";
import { fetcher } from "src/services/config";
import { MethodType } from "src/services/type";

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const Step2: React.FC<Step2Props> = ({ errors }) => {
  const theme = useTheme();
  const { t } = useTranslation(["notification-page"]);
  const { state, dispatch } = useNotificationState(); //GLOBAL STATE NOTIFICATIONS
  const [downloadState, setDownloadState] = React.useState<any>({
    email: "",
    applicative: "",
    push_mobile: "",
  });
  const { id, idNotification } = useParams();

  const [open, setOpen] = React.useState(false);

  // Templates created with the new Puck editor are already clean MJML-compiled
  // HTML — skip the pipeline to avoid round-trip drift.
  // Old SunEditor HTML (or hybrid MJML+SunEditor) needs the pipeline to strip
  // editor UI styles that browsers render but email clients ignore.
  const previewHtml = useMemo(() => {
    const html = state.email_desc;
    if (!html) return "";
    try {
      const doc = new DOMParser().parseFromString(html, "text/html");
      const hasSunEditorContent =
        html.includes("se-component") ||
        html.includes("__se__") ||
        html.includes("sun-editor");
      if (isMjmlTemplate(doc) && !hasSunEditorContent) return html;
      return generateEmailHtml(convertHtmlToPuckData(html));
    } catch {
      return html;
    }
  }, [state.email_desc]);

  const handleClick = () => {
    setOpen(true);
  };

  const [isMailEditorOpen, setIsMailEditorOpen] = useState(false);
  const [confirmCloseMailEditor, setConfirmCloseMailEditor] = useState(false);

  const [mailEditorKey, setMailEditorKey] = useState(0);

  const emailSource = useRef<"mailEditor" | "sunEditor" | "raw">("raw");

  const { columns, dataDetail, actionsForForms, mutate } = useFetchDetail(
    ServicesURL.getCorporate,
    id || "",
    PagesID.corporate,
  );

  const {
    dataDetail: translatedHtmlTemplates,
    mutate: mutateTranslatedHtmlTemplates,
  } = useFetchDataDetail(
    idNotification
      ? `${ServicesURL.getNotificationSecondaryTemplate.replace("idCorporate", id ? id : "1").replace("notificationId", idNotification)}?outputChannel=${"EMAIL"}`
      : null,
  );

  const { trigger: triggerUpdateSecondaryTemplate } = useSWRMutation(
    `${ServicesURL.putNotificationSecondaryTemplate.replace("idCorporate", id ? id : "1")}`,
    (url: string, arg: { arg: any }) => {
      return fetcher<any>(
        `${url.replace("notificationId", arg.arg.notificationId ? arg.arg.notificationId : "")}`,
        MethodType.PUT,
        {
          ...arg.arg,
        },
      );
    },
  );

  const MAX_SIZE_UPLOAD = 10 * 1024 * 1024; // 10 Mb

  useEffect(() => {
    const zipAttachments = getZipWithoutDownload(
      `${ServicesURL.downloadNotificationAttachments.replace("idCorporate", id ? id : "1").replace("notificationId", idNotification ? idNotification : "")}`,
    ).then((zipAttachments) => {
      if (zipAttachments) {
        unzipZipFile(zipAttachments).then(
          (extractedFiles: { fileName: string; file: File }[]) => {
            dispatch({
              type: "SET_INPUT_VALUES",
              payload: {
                key: "attachments",
                value: extractedFiles.map((item) => item.file),
              },
            });
          },
        );
      }
    });
  }, []);

  const { dataTable: dataEvents } = useFetchTable(
    `${ServicesURL.getEvents}`,
    PagesID["corporate.notifications"],
  );

  const optionsEvents = useMemo(() => {
    if (!dataEvents) return;
    return dataEvents.map((el: ResponseEvents) => {
      return {
        value: el.name,
        ...el,
      };
    });
  }, [dataEvents]);

  const handleSwitchChange = (type: ListNotificationType) => {
    //ON SWITCH CHANGE
    const flagKey = getFlagKey(type);
    const uploadKey = getUploadKey(type);
    const isSwitchOn = !state[flagKey];

    dispatch({
      type: "SET_INPUT_VALUES",
      payload: { key: flagKey, value: isSwitchOn },
    }); //SET CATEGORY FLAG VALUE

    if (!isSwitchOn) {
      //IF SWITCH OFF REMOVE FILES UPLOADED IF EXIST FOR CURRENT CATEGORY
      dispatch({
        type: "SET_INPUT_VALUES",
        payload: { key: uploadKey, value: [] },
      });
    }
  };

  const [openUpload, setOpenUpload] = useState<ActionUploadDownloadIterface>();
  const { t: modalT } = useTranslation(["modal"]);

  const handleDownload = async (type: ListNotificationType) => {
    //TODO: DOWNLOAD FILE
    setDownloadState((prevState: any) => ({
      ...prevState,
      [type]: "downloading",
    }));

    await new Promise((resolve) => setTimeout(resolve, 2000));

    setDownloadState((prevState: any) => ({
      ...prevState,
      [type]: "completed",
    }));
  };

  const getButtonText = (type: ListNotificationType) => {
    //DOWNLOAD BUTTON LABEL
    switch (downloadState[type]) {
      case "downloading":
        return t("in_progress");
      case "completed":
        return t("downloaded");
      default:
        return t("download");
    }
  };

  const getButtonIcon = (type: ListNotificationType) => {
    //BUTTON DOWNLOAD ICON
    switch (downloadState[type]) {
      case "downloading":
        return <CircularProgress size={20} />;
      case "completed":
        return <CheckCircleOutlineIcon />;
      default:
        return <DownloadIcon />;
    }
  };

  const availableTags =
    optionsEvents.find((item: ValuesInterface) => item.value === state["event"])
      ?.availableTags ?? [];

  const sortedTags = [...availableTags].sort((a, b) =>
    a.label.localeCompare(b.label),
  );

  const typeVisibilityMap: Record<string, string[]> = {
    CUMULATIVELEARNREM: ["email"],
    WELCOME: ["email"],
    WELCOMENP: ["email"],
    RESET: ["email"],
    DEFAULT: ["email", "applicative", "push_mobile"],
  };

  const visibleTypes =
    typeVisibilityMap[state.event] || typeVisibilityMap.DEFAULT;

  const flagsCC = ["flagCcSingleUog", "flagCcAllUog"];

  const isDisabled = (flagKey: string) => {
    if (
      flagsCC.includes(flagKey) &&
      flagsCC.map((item) => state[item]).some((item) => item === true)
    ) {
      return !state[flagKey];
    }
    return false;
  };

  // Languages
  const { dataDetail: languagesData } = useFetchDataDetail(
    `${ServicesURL.corporateLanguages.replace(":corporateId", id!.toString())}`,
  );

  const languages = useTranslateInput(languagesData);

  const languageLabels = useMemo(() => {
    const map: Record<string, string> = {};
    (languages?.secondary_languages || []).forEach(
      (lang: { id: string; label: string }) => {
        map[lang.id] = lang.label;
      },
    );
    return map;
  }, [languages]);

  const hasSecondaryLanguages =
    languageLabels && Object.keys(languageLabels).length > 0;

  const closeMessage = hasSecondaryLanguages
    ? "Sei sicuro di voler uscire?"
    : "Sei sicuro di voler uscire senza salvare?";

  return (
    <>
      <Box
        sx={{
          backgroundColor: theme.palette.background.paper,
          padding: "16px",
          borderRadius: "3px",
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px",
            marginBottom: "16px",
          }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={!!state.flagTranslate}
                onChange={() =>
                  dispatch({
                    type: "SET_INPUT_VALUES",
                    payload: {
                      key: "flagTranslate",
                      value: !state.flagTranslate,
                    },
                  })
                }
              />
            }
            label={t("activate_template_translation")}
            labelPlacement="end"
            sx={{ marginRight: "auto", paddingBottom: "8px" }}
          />
        </Box>
        <Typography variant="body1" gutterBottom>
          {t("template")}
        </Typography>
        <Grid container spacing={3}>
          {visibleTypes.map((type) => {
            const typeKey = type.toLowerCase() as ListNotificationType;
            const flagKey = getFlagKey(typeKey);
            // const uploadKey = getUploadKey(typeKey);

            return (
              <Grid item xs={12} key={type}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "16px",
                    marginBottom: "16px",
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      minWidth: "150px",
                      marginRight: "16px",
                      fontWeight: "bold",
                    }}
                  >
                    {t(type)}
                  </Typography>
                  <Box
                    sx={{
                      width: "50%", //TODO: REMOVE
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          checked={state[flagKey]}
                          onChange={() => handleSwitchChange(typeKey)}
                        />
                      }
                      label={`${t("activate_sending_with")} ${t(typeKey)}`}
                      labelPlacement="end"
                      sx={{ marginRight: "auto", paddingBottom: "8px" }}
                    />
                    {/*DOWNLOAD & UPLOAD TEMPLATES TEMPORARY HIDED */}
                    {/*<Box*/}
                    {/*  sx={{*/}
                    {/*    flex: 1,*/}
                    {/*    display: "flex",*/}
                    {/*    justifyContent: "flex-end",*/}
                    {/*    gap: 1,*/}
                    {/*  }}*/}
                    {/*>*/}
                    {/*  <Button*/}
                    {/*    sx={{*/}
                    {/*      marginRight: "8px",*/}
                    {/*      backgroundColor: theme.palette.background.paper,*/}
                    {/*      color: theme.palette.primary.main,*/}
                    {/*      border: `1px solid ${theme.palette.primary.main}`,*/}
                    {/*      flex: 1,*/}
                    {/*      whiteSpace: "nowrap",*/}
                    {/*      borderRadius: "3px",*/}
                    {/*    }}*/}
                    {/*    startIcon={getButtonIcon(typeKey)}*/}
                    {/*    onClick={() => handleDownload(typeKey)}*/}
                    {/*    disabled={!state[flagKey]}*/}
                    {/*  >*/}
                    {/*    {getButtonText(typeKey)}*/}
                    {/*  </Button>*/}
                    {/*  <Button*/}
                    {/*    component="label"*/}
                    {/*    sx={{*/}
                    {/*      backgroundColor: theme.palette.primary.main,*/}
                    {/*      color: theme.palette.text.primary,*/}
                    {/*      flex: 1,*/}
                    {/*      borderRadius: "3px",*/}
                    {/*      whiteSpace: "nowrap",*/}
                    {/*    }}*/}
                    {/*    disabled={!state[flagKey]}*/}
                    {/*  >*/}
                    {/*    {!!state[uploadKey] && state[uploadKey].length !== 0*/}
                    {/*      ? t("uploaded")*/}
                    {/*      : t("upload")}*/}
                    {/*    <VisuallyHiddenInput*/}
                    {/*      type="file"*/}
                    {/*      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>*/}
                    {/*        handleFileChange(e, typeKey, state, dispatch)*/}
                    {/*      }*/}
                    {/*    />*/}
                    {/*  </Button>*/}
                    {/*</Box>*/}
                  </Box>
                </Box>

                {state[flagKey] && (
                  <div
                    style={{
                      display: "flex",
                      gap: "10px",
                      alignItems: "stretch",
                    }}
                  >
                    <Box
                      sx={{
                        backgroundColor: theme.palette.background.paper,
                        padding: "16px",
                        marginY: "25px",
                        borderRadius: "3px",
                        border: `1px solid ${theme.palette.divider}`,
                        flex: 8,
                        minWidth: 0,
                      }}
                    >
                      {/*<Typography variant="subtitle1" gutterBottom>*/}
                      {/*  {t("additional_settings_for")} {t(typeKey)}*/}
                      {/*</Typography>*/}

                      {/* Form condizionale in base a typeKey */}
                      {typeKey === "email" && (
                        <>
                          <SelectCst
                            label={`${t("email_sender")}`}
                            key={"email_sender"}
                            id={"email_sender"}
                            value={state["email_sender"]}
                            required={true}
                            onChange={(e: any) => {
                              dispatch({
                                type: "SET_INPUT_VALUES",
                                payload: { key: "email_sender", value: e },
                              });
                            }}
                            sx={{
                              marginY: "16px",
                            }}
                            options={
                              dataDetail?.sender_emails?.map((email: any) => ({
                                label: email,
                                value: email,
                              })) || []
                            }
                            error={!!errors["email_sender"]}
                            errorMessage={`${t(`validation.${errors["email_sender"]}`)}`}
                          />
                          <SelectCst
                            label={`${t("email_display_name")}`}
                            key={"email_display_name"}
                            id={"email_display_name"}
                            value={state["email_display_name"]}
                            required={false}
                            onChange={(e: any) => {
                              dispatch({
                                type: "SET_INPUT_VALUES",
                                payload: {
                                  key: "email_display_name",
                                  value: e,
                                },
                              });
                            }}
                            sx={{
                              marginY: "16px",
                            }}
                            options={
                              dataDetail?.display_names?.map((email: any) => ({
                                label: email,
                                value: email,
                              })) || []
                            }
                            error={!!errors["email_display_name"]}
                            errorMessage={`${t(`validation.${errors["email_display_name"]}`)}`}
                          />
                          <InputCst
                            label={`${t("subject")}`}
                            key={"email_subject"}
                            id={"email_subject"}
                            value={state["email_subject"]}
                            required={true}
                            maxValue={100}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>,
                            ) => {
                              dispatch({
                                type: "SET_INPUT_VALUES",
                                payload: {
                                  key: e.target.id,
                                  value: e.target.value,
                                },
                              });
                            }}
                            sx={{
                              marginY: "16px",
                            }}
                            error={!!errors["email_subject"]}
                            errorMessage={`${t(`validation.${errors["email_subject"]}`)}`}
                            endAdornment={
                              <InputAdornment position="end">
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: theme.palette.text.primary,
                                    mr: 1,
                                  }}
                                >
                                  {`${state["email_subject"]?.length || 0}/100`}
                                </Typography>
                              </InputAdornment>
                            }
                            notificationLanguages={
                              state.flagTranslate &&
                              state.emailSubject != null &&
                              Object.keys(
                                translatedHtmlTemplates?.templates ?? {},
                              ).length > 0
                                ? languages
                                : null
                            }
                            idNotification={idNotification}
                            outputChannel="EMAIL"
                            notificationPropertyName="subject"
                          />
                          <div
                            style={{ marginTop: "16px", marginBottom: "16px" }}
                          >
                            <ButtonCst
                              id="openMailEditorBtn"
                              variant="outlined"
                              onClick={() => {
                                setMailEditorKey((k) => k + 1);
                                setIsMailEditorOpen(true);
                              }}
                              sx={{ width: "100%" }}
                            >
                              {t("open-editor")}
                            </ButtonCst>
                          </div>

                          {state.email_desc ? (
                            <iframe
                              srcDoc={previewHtml}
                              style={{
                                width: "100%",
                                minHeight: "400px",
                                border: `1px solid ${theme.palette.divider}`,
                                borderRadius: "4px",
                              }}
                              sandbox="allow-same-origin"
                              title="Anteprima email"
                            />
                          ) : (
                            <Box
                              sx={{
                                width: "100%",
                                minHeight: "400px",
                                border: `1px solid ${theme.palette.divider}`,
                                borderRadius: "4px",
                                backgroundColor: theme.palette.background.paper,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <Typography
                                variant="h6"
                                sx={{
                                  color: theme.palette.text.primary,
                                  fontWeight: "bold",
                                }}
                              >
                                Preview non disponibile
                              </Typography>
                            </Box>
                          )}

                          <Dialog
                            fullScreen
                            open={isMailEditorOpen}
                            onClose={() => setConfirmCloseMailEditor(true)}
                            TransitionComponent={Transition}
                            PaperProps={{
                              sx: {
                                overflow: "hidden",
                                display: "flex",
                                flexDirection: "column",
                              },
                            }}
                          >
                            <Box sx={{ flex: 1, overflow: "hidden" }}>
                              <MailEditor
                                key={mailEditorKey}
                                initialHtml={state.email_desc}
                                notificationName={state.notificationName}
                                templates={translatedHtmlTemplates?.templates}
                                isEditMode={
                                  !!idNotification &&
                                  !!state.flagTranslate &&
                                  Object.keys(
                                    translatedHtmlTemplates?.templates ?? {},
                                  ).length > 0
                                }
                                languageLabels={
                                  state.flagTranslate &&
                                  state.emailBody != null &&
                                  Object.keys(
                                    translatedHtmlTemplates?.templates ?? {},
                                  ).length > 0
                                    ? languageLabels
                                    : undefined
                                }
                                onUpdateSecondaryTemplate={async (
                                  lang,
                                  html,
                                ) => {
                                  await triggerUpdateSecondaryTemplate({
                                    notificationId: idNotification,
                                    outputChannel: "EMAIL",
                                    templates: {
                                      [lang]: {
                                        description: html,
                                      },
                                    },
                                  });
                                  mutateTranslatedHtmlTemplates &&
                                    mutateTranslatedHtmlTemplates();
                                }}
                                onClose={() => setConfirmCloseMailEditor(true)}
                                onSave={(
                                  html: string,
                                  retranslateAll: boolean,
                                ) => {
                                  emailSource.current = "mailEditor";

                                  dispatch({
                                    type: "SET_INPUT_VALUES",
                                    payload: { key: "email_desc", value: html },
                                  });

                                  dispatch({
                                    type: "SET_INPUT_VALUES",
                                    payload: {
                                      key: "retranslateAfterSave",
                                      value: retranslateAll,
                                    },
                                  });

                                  setIsMailEditorOpen(false);
                                }}
                              />
                            </Box>
                            <Box
                              sx={{
                                p: 2,
                                borderTop: `1px solid ${theme.palette.divider}`,
                                bgcolor: theme.palette.background.default,
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{ mb: 1, fontWeight: "bold" }}
                              >
                                {t("availableTags_label")}
                              </Typography>
                              <Box
                                sx={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 1,
                                }}
                              >
                                {sortedTags.map((el: TagInterface) => (
                                  <Chip
                                    key={el.value}
                                    label={t(`availableTags.${el.value}`)}
                                    sx={{
                                      cursor: "pointer",
                                      backgroundColor:
                                        theme.palette.background.paper,
                                      border: `1px solid ${theme.palette.divider}`,
                                    }}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => {
                                      navigator.clipboard.writeText(el.value);
                                      handleClick();
                                    }}
                                  />
                                ))}
                              </Box>
                            </Box>
                            <Snackbar
                              open={open}
                              autoHideDuration={1000}
                              onClose={() => setOpen(false)}
                              message="Copied to clipboard"
                              anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "left",
                              }}
                            />
                          </Dialog>

                          <ModalConfirmsCst
                            open={confirmCloseMailEditor}
                            title={""}
                            description={closeMessage}
                            onCallBackClosed={() =>
                              setConfirmCloseMailEditor(false)
                            }
                            onActionConfirmed={() => {
                              setConfirmCloseMailEditor(false);
                              setIsMailEditorOpen(false);
                            }}
                          />

                          {state.event !== "ENROLLIVE" && (
                            <Accordion
                              variant="outlined"
                              sx={{ marginY: "16px" }}
                            >
                              <AccordionSummary
                                expandIcon={<ArrowDropDownIcon />}
                                aria-controls="panel2-content"
                                id="panel2-header"
                              >
                                <Typography component="span">
                                  {t("cc_options")}
                                </Typography>
                              </AccordionSummary>
                              <AccordionDetails>
                                <Stack>
                                  <InputCst
                                    label={`${t("email_cc")}`}
                                    key={"email_cc"}
                                    id={"email_cc"}
                                    value={state["email_cc"]}
                                    required={false}
                                    onChange={(
                                      e: React.ChangeEvent<HTMLInputElement>,
                                    ) => {
                                      dispatch({
                                        type: "SET_INPUT_VALUES",
                                        payload: {
                                          key: e.target.id,
                                          value: e.target.value,
                                        },
                                      });
                                    }}
                                    sx={{
                                      marginY: "16px",
                                    }}
                                  />
                                  <FormControlLabel
                                    control={
                                      <Switch
                                        checked={
                                          state[getFlagKey("cc_supervisor")]
                                        }
                                        onChange={() =>
                                          handleSwitchChange("cc_supervisor")
                                        }
                                      />
                                    }
                                    label={`${t("cc_supervisor")}`}
                                    labelPlacement="end"
                                    sx={{
                                      marginRight: "auto",
                                      paddingBottom: "8px",
                                      marginY: "16px",
                                    }}
                                  />
                                  <FormControlLabel
                                    control={
                                      <Switch
                                        checked={
                                          state[getFlagKey("cc_single_uog")]
                                        }
                                        onChange={() =>
                                          handleSwitchChange("cc_single_uog")
                                        }
                                        disabled={isDisabled(
                                          getFlagKey("cc_single_uog"),
                                        )}
                                      />
                                    }
                                    label={`${t("cc_single_uog")}`}
                                    labelPlacement="end"
                                    sx={{
                                      marginRight: "auto",
                                      paddingBottom: "8px",
                                      marginY: "16px",
                                    }}
                                  />
                                  <FormControlLabel
                                    control={
                                      <Switch
                                        checked={
                                          state[getFlagKey("cc_all_uog")]
                                        }
                                        onChange={() =>
                                          handleSwitchChange("cc_all_uog")
                                        }
                                        disabled={isDisabled(
                                          getFlagKey("cc_all_uog"),
                                        )}
                                      />
                                    }
                                    label={`${t("cc_all_uog")}`}
                                    labelPlacement="end"
                                    sx={{
                                      marginRight: "auto",
                                      paddingBottom: "8px",
                                      marginY: "16px",
                                    }}
                                  />
                                </Stack>
                              </AccordionDetails>
                            </Accordion>
                          )}

                          <Grid key={"attachments"} item xs={12} sm={12}>
                            <label htmlFor={"attachments"}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  marginY: "16px",
                                  justifyContent: "space-between",
                                  border: `1px dashed ${theme.palette.divider}`,
                                  paddingLeft: theme.spacing(3.3),
                                  paddingRight: theme.spacing(0.3),
                                  cursor: "pointer",
                                  position: "relative",
                                  "&:hover": {
                                    backgroundColor: theme.palette.action.hover,
                                  },
                                }}
                              >
                                <Typography variant="body1" component="span">
                                  {`${t("email_attachments")}`}
                                </Typography>

                                <IconButton
                                  component="span"
                                  sx={{ marginLeft: 1 }}
                                >
                                  <UploadIcon />
                                </IconButton>

                                <VisuallyHiddenInput
                                  id={"attachments"}
                                  type="file"
                                  accept=".pdf, .jpg, .jpeg, .png, .csv, .xls, .xlsx, .doc, .docx"
                                  multiple
                                  onChange={(e: any) =>
                                    handleMultiFileChange(
                                      e,
                                      "attachments",
                                      dispatch,
                                      MAX_SIZE_UPLOAD,
                                      1024,
                                      state.attachments,
                                    )
                                  }
                                  error={!!state["error_file_attachments"]}
                                  errorMessage={`${t(
                                    `validation.${state["error_file_attachments"]}`,
                                    { maxSize: bytesToMB(MAX_SIZE_UPLOAD) },
                                  )}`}
                                />
                              </Box>
                            </label>

                            {state?.attachments?.length > 0 && (
                              <List dense>
                                {state?.attachments?.map((item: File) => (
                                  <ListItem
                                    key={item.name}
                                    secondaryAction={
                                      <>
                                        <IconButton
                                          edge="end"
                                          aria-label="open"
                                          onClick={() => {
                                            handleDownloadFile(item);
                                          }}
                                        >
                                          <FileDownloadIcon />
                                        </IconButton>

                                        <IconButton
                                          edge="end"
                                          aria-label="delete"
                                          onClick={() =>
                                            handleMultiFileDelete(
                                              "attachments",
                                              dispatch,
                                              item.name,
                                              state.attachments,
                                            )
                                          }
                                        >
                                          <DeleteIcon />
                                        </IconButton>
                                      </>
                                    }
                                  >
                                    <ListItemText primary={item.name} />
                                  </ListItem>
                                ))}
                              </List>
                            )}
                          </Grid>
                        </>
                      )}

                      {typeKey === "push_mobile" && (
                        <>
                          <InputCst
                            label={`${t("subject")}`}
                            key={"push_mobile_subject"}
                            id={"push_mobile_subject"}
                            value={state["push_mobile_subject"]}
                            required={true}
                            maxValue={100}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>,
                            ) => {
                              dispatch({
                                type: "SET_INPUT_VALUES",
                                payload: {
                                  key: e.target.id,
                                  value: e.target.value,
                                },
                              });
                            }}
                            sx={{
                              marginY: "16px",
                            }}
                            error={!!errors["push_mobile_subject"]}
                            errorMessage={`${t(`validation.${errors["push_mobile_subject"]}`)}`}
                            endAdornment={
                              <InputAdornment position="end">
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: theme.palette.text.primary,
                                    mr: 1,
                                  }}
                                >
                                  {`${state["push_mobile_subject"]?.length || 0}/100`}
                                </Typography>
                              </InputAdornment>
                            }
                            notificationLanguages={
                              state.flagTranslate && state.pushSubject != null
                                ? languages
                                : null
                            }
                            idNotification={idNotification}
                            outputChannel="PUSH"
                            notificationPropertyName="subject"
                          />
                          <InputCst
                            label={`${t("description")}`}
                            key={"push_mobile_desc"}
                            id={"push_mobile_desc"}
                            value={state["push_mobile_desc"]}
                            required={true}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>,
                            ) => {
                              dispatch({
                                type: "SET_INPUT_VALUES",
                                payload: {
                                  key: e.target.id,
                                  value: e.target.value,
                                },
                              });
                            }}
                            sx={{
                              marginY: "16px",
                            }}
                            multiline
                            rows={6}
                            error={!!errors["push_mobile_desc"]}
                            errorMessage={`${t(`validation.${errors["push_mobile_desc"]}`)}`}
                            notificationLanguages={
                              state.flagTranslate && state.pushBody != null
                                ? languages
                                : null
                            }
                            idNotification={idNotification}
                            outputChannel="PUSH"
                            notificationPropertyName="description"
                          />
                        </>
                      )}

                      {typeKey === "applicative" && (
                        <>
                          <InputCst
                            label={`${t("subject")}`}
                            key={"applicative_subject"}
                            id={"applicative_subject"}
                            value={state["applicative_subject"]}
                            required={true}
                            maxValue={100}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>,
                            ) => {
                              dispatch({
                                type: "SET_INPUT_VALUES",
                                payload: {
                                  key: e.target.id,
                                  value: e.target.value,
                                },
                              });
                            }}
                            sx={{
                              marginY: "16px",
                            }}
                            error={!!errors["applicative_subject"]}
                            errorMessage={`${t(`validation.${errors["applicative_subject"]}`)}`}
                            endAdornment={
                              <InputAdornment position="end">
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: theme.palette.text.primary,
                                    mr: 1,
                                  }}
                                >
                                  {`${state["applicative_subject"]?.length || 0}/100`}
                                </Typography>
                              </InputAdornment>
                            }
                            notificationLanguages={
                              state.flagTranslate &&
                              state.applicativeSubject != null
                                ? languages
                                : null
                            }
                            idNotification={idNotification}
                            outputChannel="WEB"
                            notificationPropertyName="subject"
                          />
                          <InputCst
                            label={`${t("description")}`}
                            key={"applicative_desc"}
                            id={"applicative_desc"}
                            value={state["applicative_desc"]}
                            required={true}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>,
                            ) => {
                              dispatch({
                                type: "SET_INPUT_VALUES",
                                payload: {
                                  key: e.target.id,
                                  value: e.target.value,
                                },
                              });
                            }}
                            sx={{
                              marginY: "16px",
                            }}
                            multiline
                            rows={6}
                            error={!!errors["applicative_desc"]}
                            errorMessage={`${t(`validation.${errors["applicative_desc"]}`)}`}
                            notificationLanguages={
                              state.flagTranslate &&
                              state.applicativeBody != null
                                ? languages
                                : null
                            }
                            idNotification={idNotification}
                            outputChannel="WEB"
                            notificationPropertyName="description"
                          />
                        </>
                      )}
                    </Box>
                    <Box
                      sx={{
                        backgroundColor: theme.palette.background.paper,
                        padding: "16px",
                        marginY: "25px",
                        borderRadius: "3px",
                        border: `1px solid ${theme.palette.divider}`,
                        flex: 2,
                      }}
                    >
                      <Stack direction="column" spacing={1}>
                        {sortedTags.map((el: TagInterface) => (
                          <Chip
                            key={el.value}
                            label={t(`availableTags.${el.value}`)}
                            sx={{ cursor: "pointer" }}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => {
                              navigator.clipboard.writeText(el.value);
                              handleClick();
                            }}
                          />
                        ))}
                      </Stack>
                      <Snackbar
                        open={open}
                        autoHideDuration={1000}
                        onClose={() => setOpen(false)}
                        message="Copied to clipboard"
                        anchorOrigin={{
                          vertical: "bottom",
                          horizontal: "left",
                        }}
                        sx={{ zIndex: 99999 }}
                      />
                    </Box>
                  </div>
                )}
              </Grid>
            );
          })}
        </Grid>
      </Box>
    </>
  );
};

export default Step2;
